import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@/i18n/hook';
import { pillarArticles } from '@/mocks/articles';
import { buildCanonicalUrl, ARTICLE_HERO_IMAGE } from '@/seo/site';

const ARTICLE_KEYWORDS =
  'CSSBuy Spreadsheet, CSSBuy guide, CSSBuy shipping, CSSBuy QC, CSSBuy review, CSSBuy warehouse';

interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
  ogType: string;
  ogImage: string;
  twitterCard: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  robots: string;
}

function readMeta(attr: 'name' | 'property', key: string): string {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  return el?.getAttribute('content') ?? '';
}

function readCanonical(): string {
  const el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  return el?.getAttribute('href') ?? '';
}

function writeMeta(attr: 'name' | 'property', key: string, content: string): void {
  if (!content) {
    document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)?.remove();
    return;
  }
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function writeCanonical(href: string): void {
  if (!href) {
    document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.remove();
    return;
  }
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function applySeo(seo: SeoData): void {
  document.title = seo.title;
  writeMeta('name', 'description', seo.description);
  writeMeta('name', 'keywords', seo.keywords);
  writeMeta('name', 'robots', seo.robots);
  writeMeta('property', 'og:title', seo.ogTitle);
  writeMeta('property', 'og:description', seo.ogDescription);
  writeMeta('property', 'og:url', seo.ogUrl);
  writeMeta('property', 'og:type', seo.ogType);
  writeMeta('property', 'og:image', seo.ogImage);
  writeMeta('name', 'twitter:card', seo.twitterCard);
  writeMeta('name', 'twitter:title', seo.twitterTitle);
  writeMeta('name', 'twitter:description', seo.twitterDescription);
  writeMeta('name', 'twitter:image', seo.twitterImage);
  writeCanonical(seo.canonical);
}

function captureInitialHead(): SeoData {
  return {
    title: document.title,
    description: readMeta('name', 'description'),
    keywords: readMeta('name', 'keywords'),
    canonical: readCanonical(),
    ogTitle: readMeta('property', 'og:title'),
    ogDescription: readMeta('property', 'og:description'),
    ogUrl: readMeta('property', 'og:url'),
    ogType: readMeta('property', 'og:type'),
    ogImage: readMeta('property', 'og:image'),
    twitterCard: readMeta('name', 'twitter:card'),
    twitterTitle: readMeta('name', 'twitter:title'),
    twitterDescription: readMeta('name', 'twitter:description'),
    twitterImage: readMeta('name', 'twitter:image'),
    robots: readMeta('name', 'robots'),
  };
}

const localize = (l: { pt: string; en: string; de: string }, lang: string): string => {
  if (lang === 'de') return l.de;
  if (lang === 'en') return l.en;
  return l.pt;
};

export default function SeoManager() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const initialRef = useRef<SeoData | null>(null);
  if (initialRef.current === null) {
    initialRef.current = captureInitialHead();
  }

  useEffect(() => {
    const pathname = location.pathname;
    const initial = initialRef.current as SeoData;

    let seo: SeoData;

    if (pathname === '/') {
      seo = initial;
    } else {
      const isGuide = pathname === '/article/cssbuy-spreadsheet-guide';
      const pillar = isGuide
        ? undefined
        : pillarArticles.find((a) => `/article/${a.slug}` === pathname);

      if (!isGuide && !pillar) {
        const notFoundTitle = t('notFound.title');
        const notFoundSubtitle = t('notFound.subtitle');
        seo = {
          title: notFoundTitle,
          description: notFoundSubtitle,
          keywords: '',
          canonical: '',
          ogTitle: notFoundTitle,
          ogDescription: notFoundSubtitle,
          ogUrl: '',
          ogType: 'website',
          ogImage: '',
          twitterCard: 'summary',
          twitterTitle: notFoundTitle,
          twitterDescription: notFoundSubtitle,
          twitterImage: '',
          robots: 'noindex, nofollow',
        };
      } else {
        const title = isGuide ? t('article.title') : localize(pillar!.title, currentLang);
        const description = isGuide ? t('article.meta') : localize(pillar!.meta, currentLang);
        const image = isGuide ? ARTICLE_HERO_IMAGE : pillar!.image;
        const canonical = buildCanonicalUrl(pathname);
        seo = {
          title,
          description,
          keywords: ARTICLE_KEYWORDS,
          canonical,
          ogTitle: title,
          ogDescription: description,
          ogUrl: canonical,
          ogType: 'article',
          ogImage: image,
          twitterCard: 'summary_large_image',
          twitterTitle: title,
          twitterDescription: description,
          twitterImage: image,
          robots: '',
        };
      }
    }

    applySeo(seo);
  }, [location.pathname, currentLang, t]);

  return null;
}